import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {from, Observable, throwError} from 'rxjs';
import {Router} from '@angular/router';
import {catchError, map, switchMap} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {AppState} from '../../reducers';
import {INFO, LOGIN_REDIRECT, ROOT_PUBLIC} from '@core/services/routes.service';
import {TokenStorageService} from '@core/modules/rest/authentication/token-storage.service';
import {AuthRestService} from '@core/modules/rest/authentication/auth-rest.service';
import {KeycloakService} from 'keycloak-angular';

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {

    private BEARER = 'Bearer ';

    constructor(private store: Store<AppState>, private router: Router, private tokenStorage: TokenStorageService, private authService: AuthRestService, private keycloakService: KeycloakService) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const securedRoute = request.url.indexOf('secured/') > -1;
        if (securedRoute) {
            const isExpired = this.keycloakService.isTokenExpired();
            if (isExpired) {
                this.redirectToLogin();
                return throwError('Token expired. You have to login again');
            }
            return from(this.keycloakService.getToken())
                .pipe(
                    switchMap(token => {
                        const headers = request.headers.set('Authorization', this.BEARER + token);
                        request = request.clone({
                            headers
                        });
                        return next.handle(request).pipe(
                            map((event: HttpEvent<any>) => {
                                return event;
                            }),
                            catchError((httpErrorResponse: HttpErrorResponse, _: Observable<HttpEvent<any>>) => {
                                    switch (httpErrorResponse.status) {
                                        case 401:
                                            this.router.navigate([ROOT_PUBLIC]);
                                            break;
                                        case 409:
                                            break;
                                    }
                                    return throwError(httpErrorResponse);
                                }
                            )
                        );
                    }),
                    catchError((resp: HttpErrorResponse, _: Observable<HttpEvent<any>>) => {
                        if (resp) {
                            if (resp.status === 401) {
                                this.keycloakService.clearToken();
                                this.router.navigate([ROOT_PUBLIC]);
                                return throwError('Unauthorized. Going to public.');
                            } else if (resp.status === 500) {
                                this.router.navigate([INFO]);
                                return throwError('Service error. Check application logs.');
                            } else {
                                this.router.navigate([INFO]);
                                return throwError('Not Found.');
                            }
                        } else {
                            this.redirectToLogin();
                            return throwError('You have to login again');
                        }
                    })
                );
        } else {
            return this.proceedWithRequest(request, next);
        }
    }

    private redirectToLogin(): void {
        const redirectUrl = encodeURIComponent(window.location.href);
        this.router.navigate([LOGIN_REDIRECT(redirectUrl)]);
    }

    redirectTo(uri: string): void {
        this.router.navigateByUrl('/', {skipLocationChange: true}).then(() =>
            this.router.navigate([uri]));
    }

    proceedWithRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            map((event: HttpEvent<any>) => {
                return event;
            }));
    }
}
